import CustomButton from "__utilities/components/CustomButton";
import LoadingSpinner from "__utilities/components/LoadingSpinner/LoadingSpinner";
import usePayment, { PollResult } from "_data/hooks/payment/usePayment";
import { logEvent } from "analytics/analytics";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { resolveOrderId } from "_navigation/paymentRoutes";

/**
 * Where the customer lands after paying (the backend's Payment:AcceptUrl).
 *
 * Arriving here proves nothing. The payment is confirmed by a signed webhook from the
 * provider to the backend, on a separate connection that can arrive after the browser
 * redirect does. So this page starts out neutral -- "we are confirming your payment" -- and
 * only claims success once the backend says the licence is Premium.
 *
 * When the wait runs out we say the payment is being processed. We do not show an error and
 * we do not invite the customer to pay again: at that point the payment has almost certainly
 * succeeded and only our record of it is late.
 *
 * Nothing is read from the query string. The provider appends its own parameters and the
 * backend ignores them, because anything the browser carries could be forged.
 */
const ConfirmationState = {
  Confirming: "Confirming",
  Confirmed: "Confirmed",
  /** Ran out of patience, and the order is not recorded yet either. */
  Processing: "Processing",
  /** Ran out of patience, but the order *is* recorded -- only the licence is catching up. */
  Recorded: "Recorded",
};

export default function OnPaymentSuccessView() {
  const { pollUntilPremium } = usePayment();
  const { setUserInfo } = useOutletContext();
  const { id } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState(ConfirmationState.Confirming);
  const hasStarted = useRef(false);
  const isUnmounted = useRef(false);

  const onGoToHome = () => {
    navigate("/");
    window.location.reload();
  };

  useEffect(() => {
    // The route id is preferred, but only once it looks like one; otherwise this falls back to
    // what we stored before leaving for the provider, which is also what makes this page work
    // when opened cold. With neither, there is nothing to confirm.
    const orderId = resolveOrderId(id);

    if (!hasStarted.current) {
      hasStarted.current = true;

      if (!orderId) {
        navigate("/account");
      } else {
        pollUntilPremium(orderId, {
          onUserInfo: (userInfo) => {
            if (!isUnmounted.current) {
              setUserInfo(userInfo);
            }
          },
          isCancelled: () => isUnmounted.current,
        }).then(({ result, orderRecorded }) => {
          if (isUnmounted.current) {
            return;
          }
          if (result === PollResult.Confirmed) {
            setState(ConfirmationState.Confirmed);

            // Fires on the backend confirming the licence, not on the customer clicking the
            // button afterwards. On the button it was wrong in both directions: it also fired
            // from the two timeout states, which are not confirmed conversions, and it never
            // fired for the customer who simply closed the tab once they saw "Betaling
            // gennemført" -- which is most of them.
            logEvent(
              "completed_payment",
              "app_upgrade_complete",
              "payment_confirmed",
            );
            return;
          }
          // Out of patience, not out of luck. Neither branch is an error.
          setState(
            orderRecorded
              ? ConfirmationState.Recorded
              : ConfirmationState.Processing,
          );
        });
      }
    }

    return () => {
      isUnmounted.current = true;
    };
  }, []);

  return (
    <div className="flex flex-col w-full h-full justify-center items-center">
      <div className="flex flex-col w-1/2 h-1/2 p-3 justify-around items-center border border-lightGrey rounded-xl">
        {state === ConfirmationState.Confirming && (
          <>
            <p className="text-[34px] font-latoBold">
              Vi bekræfter din betaling
            </p>
            <div className="flex flex-col w-full justify-center items-center gap-y-4">
              <LoadingSpinner />
              <p className="text-standard font-latoBold text-center">
                Det tager normalt kun få sekunder. Vent venligst.
              </p>
            </div>
          </>
        )}

        {state === ConfirmationState.Confirmed && (
          <>
            <p className="text-[34px] font-latoBold">Betaling gennemført</p>
            <div className="flex flex-col w-full justify-center items-center">
              <p className="text-[24px] font-latoBold">Tak!</p>
              <p className="text-standard font-latoBold">
                Vi har modtaget din betaling
              </p>
            </div>
            <CustomButton
              text={"Gå til startside"}
              onClick={onGoToHome}
              styles={"px-3 rounded-xl"}
            />
          </>
        )}

        {state === ConfirmationState.Recorded && (
          <>
            <p className="text-[34px] font-latoBold">
              Din betaling er registreret
            </p>
            <div className="flex flex-col w-full justify-center items-center">
              <p className="text-standard font-latoBold text-center">
                Din konto bliver opdateret om kort tid.
                <br />
                Du kan roligt lukke denne side.
              </p>
            </div>
            <CustomButton
              text={"Gå til startside"}
              onClick={onGoToHome}
              styles={"px-3 rounded-xl"}
            />
          </>
        )}

        {state === ConfirmationState.Processing && (
          <>
            <p className="text-[34px] font-latoBold">Din betaling behandles</p>
            <div className="flex flex-col w-full justify-center items-center">
              <p className="text-standard font-latoBold text-center">
                Vi har modtaget din betaling, men den er endnu ikke registreret.
                <br />
                Din konto bliver opdateret om kort tid, og du kan roligt lukke
                denne side.
              </p>
            </div>
            <CustomButton
              text={"Gå til startside"}
              onClick={onGoToHome}
              styles={"px-3 rounded-xl"}
            />
          </>
        )}
      </div>
    </div>
  );
}
